import { QueryClient, QueryFunction } from "@tanstack/react-query";
import {
  setAttributionCtxProvider,
  getAttributionCtxGlobal,
} from "@/hooks/useAttribution";
import { buildPartnerRequestHeaders } from "@shared/utils/partnerRequestHeaders";
import { getClientHeader, X_CLIENT_HEADER_VALUE } from "@/lib/clientHeader";
import { createApiClient } from "@kudoz/api-client";
import { createWebStorage } from "@kudoz/api-client/webStorage";
import { logger } from "@/lib/logger";
import { redactAttributionContext } from "@kudoz/shared/redactDiagnostics";

// Re-export so App.tsx can register the provider once
export { setAttributionCtxProvider };

let authExpiryDispatched = false;

export function dispatchAuthExpired() {
  if (typeof window === "undefined") return;
  if (!window.localStorage.getItem("auth_token")) return;
  if (authExpiryDispatched) return;
  authExpiryDispatched = true;
  window.dispatchEvent(new CustomEvent("auth:expired"));
  // Reset guard after redirect occurs (3 s safety valve)
  setTimeout(() => {
    authExpiryDispatched = false;
  }, 3000);
}

/**
 * Error class that preserves HTTP status and any server-supplied error code,
 * so call sites (e.g. RequirePermission) can branch on auth-family failures
 * without parsing message strings or using `as any`.
 */
export class ApiError extends Error {
  status: number;
  code?: string;
  body?: unknown;
  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

interface AuthErrorBody {
  eatzyTokenExpired?: boolean;
  kudozTokenExpired?: boolean;
  code?: string;
  /**
   * Some upstream proxies (e.g. the LAM/RMP admin handlers forwarding the
   * Eatzy menu-editor error contract) return the machine-readable code as
   * `errorCode` rather than `code`. Treat both as the canonical error code
   * so call sites can branch on a single field (e.g. `duplicate_link`,
   * `invalid_question_id`, `persistence_error`).
   */
  errorCode?: string;
  error?: string;
  message?: string;
  details?: string;
}

const AUTH_ERROR_CODES = new Set([
  "TOKEN_EXPIRED",
  "INVALID_TOKEN",
  "UNAUTHORIZED",
  "KUDOZ_TOKEN_EXPIRED",
]);

async function readBodyOnce(res: Response): Promise<AuthErrorBody> {
  try {
    return (await res.clone().json()) as AuthErrorBody;
  } catch {
    return {};
  }
}

/**
 * Inspect a non-ok response and decide whether the caller should silently
 * refresh credentials and retry. Returns:
 *   - "kudoz": refresh Kudoz JWT and retry
 *   - "eatzy": refresh Eatzy access token and retry
 *   - null:    not an auth-recoverable error (let normal error handling proceed)
 */
async function detectAuthRefreshNeeded(
  res: Response
): Promise<"kudoz" | "eatzy" | null> {
  if (res.status !== 401 && res.status !== 403) return null;
  const body = await readBodyOnce(res);
  if (body.eatzyTokenExpired) return "eatzy";
  if (res.status === 401) return "kudoz";
  // 403 without eatzyTokenExpired but with an explicit auth code → still
  // treat as a Kudoz session-expiry. Plain 403 (a real RBAC denial) falls
  // through and renders normally.
  if (body.code && AUTH_ERROR_CODES.has(body.code)) return "kudoz";
  return null;
}

async function throwIfResNotOk(res: Response) {
  if (res.ok) return;

  // Always try to parse a JSON error body so call sites can branch on
  // structured fields (e.g. `errorCode: "duplicate_link"` from the LAM
  // proxy) regardless of HTTP status. Falls back to text when the response
  // is not JSON, which preserves the prior message format.
  const ct = res.headers.get("content-type") || "";
  let body: AuthErrorBody = {} as AuthErrorBody;
  let text = "";
  if (ct.includes("application/json")) {
    body = await readBodyOnce(res);
  } else {
    text = (await res.text()) || res.statusText;
  }
  const message =
    body.error ||
    body.message ||
    body.details ||
    text ||
    res.statusText ||
    `${res.status}`;

  // Accept either `code` or the upstream Eatzy `errorCode` as the canonical
  // machine-readable error code. The two are never both set on the same
  // response in practice.
  const code = body.code ?? body.errorCode;

  throw new ApiError(`${res.status}: ${message}`, res.status, code, body);
}

function getAdminLamHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (typeof window === "undefined") return headers;
  const eatzyToken = sessionStorage.getItem("adminEatzyToken");
  if (eatzyToken) {
    headers["x-eatzy-token"] = eatzyToken;
  }
  return headers;
}

function getPartnerHeaders(): Record<string, string> {
  if (typeof window === "undefined") return {};
  // Route every partner-route call through the same pure helper that
  // `shared/utils/partnerRequestHeaders.test.ts` locks. Empty / null /
  // missing values are filtered out so `requirePartner` never sees a
  // header that's set to "" / "null" / "undefined".
  return buildPartnerRequestHeaders({
    partnerUserId: localStorage.getItem("partnerUserId"),
    restaurantId: localStorage.getItem("partnerRestaurantId"),
    eatzyToken: sessionStorage.getItem("partnerEatzyToken"),
  });
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (typeof window === "undefined") {
    return headers;
  }
  const token = window.localStorage.getItem("auth_token");
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    // OAuth admins (e.g. signed in via Eatzy SSO) never have auth_token — their
    // credential lives in sessionStorage/localStorage as the Eatzy access token.
    // Sending it as Bearer ensures Cloudflare's WAF lets the request through, and
    // requireAdmin / requireSuperAdmin validate it via EatzyAuthClient.getMe().
    const eatzyToken =
      window.sessionStorage.getItem("adminEatzyToken") ??
      window.localStorage.getItem("eatzyUserToken");
    if (eatzyToken) headers.Authorization = `Bearer ${eatzyToken}`;
  }
  return headers;
}

function getAttributionHeaders(method: string): Record<string, string> {
  const writeMethod = ["POST", "PUT", "PATCH"].includes(method.toUpperCase());
  if (!writeMethod) return {};
  try {
    const ctx = getAttributionCtxGlobal();
    if (ctx) {
      const safe = redactAttributionContext(ctx);
      if (safe) {
        return { "x-attribution-context": JSON.stringify(safe) };
      }
    }
  } catch {
    // ignore
  }
  return {};
}

function buildRequestInit(
  method: string,
  url: string,
  data?: unknown
): RequestInit {
  const isPartnerRoute = url.includes("/api/partner/");
  const isAdminRoute = url.includes("/api/admin/");
  const partnerHeaders = isPartnerRoute ? getPartnerHeaders() : {};
  const adminHeaders = isAdminRoute ? getAdminLamHeaders() : {};
  const authHeaders = getAuthHeaders();
  const attributionHeaders = getAttributionHeaders(method);
  return {
    method,
    headers: {
      ...(data ? { "Content-Type": "application/json" } : {}),
      ...getClientHeader(),
      ...partnerHeaders,
      ...adminHeaders,
      ...authHeaders,
      ...attributionHeaders,
    },
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  };
}

/**
 * Endpoints that must NOT trigger silent refresh-and-retry. Avoids loops on
 * the refresh endpoints themselves and on the login flow.
 */
function isRefreshExempt(url: string): boolean {
  return (
    url.includes("/api/auth/refresh-kudoz-token") ||
    url.includes("/api/auth/refresh-eatzy-token") ||
    url.includes("/api/auth/sign-in") ||
    url.includes("/api/auth/sign-up") ||
    url.includes("/api/auth/sign-out") ||
    url.includes("/api/auth/forgot-password") ||
    url.includes("/api/auth/reset-password")
  );
}

/**
 * Try to silently refresh whichever credential the server says is bad and
 * return true if a fresh request should be attempted.
 *
 * Delegates to webApiClient (defined below) so the typed client's internal
 * deduplication and storage write are the single source of truth for
 * token refresh on the web.
 */
async function tryAuthRefresh(kind: "kudoz" | "eatzy"): Promise<boolean> {
  try {
    if (kind === "kudoz") {
      return await webApiClient.auth.refreshKudozToken();
    }
    return await webApiClient.auth.refreshEatzyToken();
  } catch (err) {
    logger.error("[queryClient] tryAuthRefresh threw:", err);
    return false;
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: { restaurantId?: string; assertContext?: () => void; signal?: AbortSignal }
): Promise<Response> {
  const init = () => {
    options?.assertContext?.();
    const request = buildRequestInit(method, url, data);
    if (options?.signal) request.signal = options.signal;
    if (options?.restaurantId) {
      request.headers = { ...request.headers, "x-restaurant-id": options.restaurantId };
    }
    return request;
  };
  let res = await fetch(url, init());

  if (!res.ok && !isRefreshExempt(url)) {
    const refreshKind = await detectAuthRefreshNeeded(res);
    if (refreshKind) {
      const refreshed = await tryAuthRefresh(refreshKind);
      if (refreshed) {
        // Rebuild init so the freshly-stored token is picked up by getAuthHeaders().
        res = await fetch(url, init());
      } else {
        // Refresh failed → session is unrecoverable. Force a clean sign-out
        // (toast + redirect to /login with returnUrl) instead of letting the
        // App-level eatzy:token-expired handler retry in a loop.
        dispatchAuthExpired();
        throw new ApiError(
          refreshKind === "eatzy" ? "eatzyTokenExpired" : "Session expired",
          res.status,
          refreshKind === "eatzy"
            ? "EATZY_TOKEN_EXPIRED"
            : "KUDOZ_TOKEN_EXPIRED"
        );
      }
    }
  }

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
/**
 * Build a TanStack Query `queryFn` that runs through the shared
 * auth-refresh-and-retry pipeline.
 *
 * - By default the URL is derived from `queryKey.join("/")`, matching the
 *   convention used across the app for plain string-keyed queries.
 * - Pass an explicit `url` to pin the endpoint regardless of the queryKey
 *   shape — useful when the queryKey carries discriminators (e.g. a user
 *   id) that must NOT appear in the request path.
 */
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
  url?: string;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior, url: fixedUrl }) =>
  async ({ queryKey }) => {
    const url = (fixedUrl ?? queryKey.join("/")) as string;

    const buildInit = (): RequestInit => {
      const isPartnerRoute = url.includes("/api/partner/");
      const isAdminRoute = url.includes("/api/admin/");
      const partnerHeaders = isPartnerRoute ? getPartnerHeaders() : {};
      const adminHeaders = isAdminRoute ? getAdminLamHeaders() : {};
      const authHeaders = getAuthHeaders();
      return {
        credentials: "include",
        headers: {
          ...getClientHeader(),
          ...partnerHeaders,
          ...adminHeaders,
          ...authHeaders,
        },
      };
    };

    let res = await fetch(url, buildInit());

    if (!res.ok && !isRefreshExempt(url)) {
      const refreshKind = await detectAuthRefreshNeeded(res);
      if (refreshKind) {
        const refreshed = await tryAuthRefresh(refreshKind);
        if (refreshed) {
          res = await fetch(url, buildInit());
        } else if (unauthorizedBehavior !== "returnNull") {
          dispatchAuthExpired();
          throw new ApiError(
            refreshKind === "eatzy" ? "eatzyTokenExpired" : "Session expired",
            res.status,
            refreshKind === "eatzy"
              ? "EATZY_TOKEN_EXPIRED"
              : "KUDOZ_TOKEN_EXPIRED"
          );
        }
      }
    }

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Singleton `@kudoz/api-client` instance for the web app.
 *
 * Provides the Phase 1 typed endpoint surface (`webApiClient.auth`,
 * `webApiClient.partner.posts`, etc.) and acts as the single source
 * of truth for token storage on the web (localStorage / sessionStorage).
 *
 * The `partnerHeadersProvider` reads the same localStorage keys that the
 * legacy `getPartnerHeaders()` function above uses, so both code paths stay
 * in sync during the transition period.
 *
 * Web-specific auth expiry dispatches the existing `auth:expired` event so
 * all existing `useAuth` listeners continue to work unchanged.
 */
export const webApiClient = createApiClient({
  baseUrl: "",
  storage: createWebStorage(),
  clientHeader: X_CLIENT_HEADER_VALUE,
  attributionProvider: () => {
    try {
      return redactAttributionContext(getAttributionCtxGlobal());
    } catch {
      return null;
    }
  },
  partnerHeadersProvider: () => {
    if (typeof window === "undefined") return {};
    return buildPartnerRequestHeaders({
      partnerUserId: localStorage.getItem("partnerUserId"),
      restaurantId: localStorage.getItem("partnerRestaurantId"),
      eatzyToken: sessionStorage.getItem("partnerEatzyToken"),
    });
  },
  onAuthExpired: dispatchAuthExpired,
});
